import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SummaryApi from "../common";                 // getStudentById & updateStudent
import { FaEye, FaEyeSlash, FaPhone } from "react-icons/fa";
import Swal from "sweetalert2";
import noimage from "../asset/noimage.png";         // fallback image

const StudentProfileEdit = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [studentId, setStudentId] = useState(null);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    ug_pg: "N/A",
    academic_course: "N/A",
    academic_year: "N/A",
    email: "",
    mobile_number: "",
    alternate_mobile_number: "N/A",
    address: "N/A",
    city: "N/A",
    student_image: "",        
    previewImage: "",           
    college_name:"",
    college_id: "",             // ← NEW: store college_id from API
    is_active: 1,
    password: "",
    is_self_registered: 0,
  });
  const [showPassword, setShowPassword] = useState(false);
const [loading, setLoading] = useState(false);
  /* --------------------------------------------------------------
     1. GET student_id from URL (base64) → fetch data
     -------------------------------------------------------------- */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const encodedId = params.get("id");
    if (!encodedId) {
      Swal.fire({ icon: "error", title: "Error", text: "No student ID in URL" });
      navigate("/student-panel/profile");
      return;
    }

    const decodedId = atob(encodedId);
    setStudentId(decodedId);
    sessionStorage.setItem("edit_student_id", decodedId);

    // ---- fetch student ----
    fetch(SummaryApi.getStudentById.url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ student_id: parseInt(decodedId) }),
    })
      .then((r) => r.json())
      .then((resp) => {
        if (resp.status && resp.data) {
          const d = resp.data;
          setFormData({
            first_name: d.first_name ?? "",
            last_name: d.last_name ?? "",
            ug_pg: d.ug_pg ?? "N/A",
            academic_course: d.academic_course ?? "N/A",
            academic_year: d.academic_year ?? "N/A",
            email: d.email ?? "",
            mobile_number: d.mobile_number ?? "",
            alternate_mobile_number: d.alternate_mobile_number ?? "N/A",
            address: d.address ?? "N/A",
            city: d.city ?? "N/A",
          college_name: d.college_id > 0 ? (d.college_name ?? "") : (d.clg_name ?? ""),
            college_id: d.college_id ?? "",                 // ← NEW: store college_id
            is_active: d.is_active ?? 1,
            password: d.password ?? "",                     // never pre-fill
            student_image: d.student_image ?? "", // URL string from server
            previewImage: "",                 // will be filled below
            is_self_registered: d.is_self_registered ?? 0,
          });

          // ---- build full image URL for preview (same logic as StudentProfile) ----
          if (d.student_image) {
            const fullUrl = `${SummaryApi.Images?.url?.replace(
              "api",
              ""
            )}${d.student_image}`;
            setFormData((prev) => ({ ...prev, previewImage: fullUrl }));
          }
        } else {
          Swal.fire({ icon: "error", title: "Failed", text: resp.message });
        }
      })
      .catch((e) => {
        console.error(e);
        Swal.fire({ icon: "error", title: "Network error", text: "Cannot load data" });
      });
  }, [location, navigate]);

  /* --------------------------------------------------------------
     2. INPUT HANDLER (text / checkbox / file)
     -------------------------------------------------------------- */
  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    if (type === "file") {
      const file = files[0];
      if (file) {
        const previewUrl = URL.createObjectURL(file);
        setFormData((prev) => ({
          ...prev,
          student_image: file,
          previewImage: previewUrl,
        }));
      }
    } else if (type === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        [name]: checked ? 1 : 0,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

/* --------------------------------------------------------------
   3. SUBMIT → multipart/form-data
   -------------------------------------------------------------- */
const handleSubmit = (e) => {
  e.preventDefault();
  if (!studentId || loading) return;
  setLoading(true);

  const payload = new FormData();
  payload.append("student_id", studentId);
  payload.append("first_name", formData.first_name);
  payload.append("last_name", formData.last_name);
  payload.append("ug_pg", formData.ug_pg);
  payload.append("academic_course", formData.academic_course);
  payload.append("academic_year", formData.academic_year);
  payload.append("email", formData.email);
  payload.append("mobile_number", formData.mobile_number);
  payload.append("alternate_mobile_number", formData.alternate_mobile_number);
  payload.append("address", formData.address);
  payload.append("city", formData.city);
  payload.append("is_active", formData.is_active);
  payload.append("is_self_registered", formData.is_self_registered);

  // ---- Conditionally pass college data ----
  if (formData.is_self_registered === 1 && formData.college_id === 0) {
    // Self-registered student: pass college_name only
    payload.append("clg_name", formData.college_name);
    payload.append("college_id", "0"); // Ensure college_id is 0 for self-registered
  } else if (formData.is_self_registered === 0 && formData.college_id > 0) {
    // College student: pass both college_id and college_name
    payload.append("college_id", formData.college_id);
    payload.append("college_name", formData.college_name);
  } else {
    // Fallback: pass both as they are
    payload.append("college_id", formData.college_id);
    payload.append("college_name", formData.college_name);
  }

  // ---- only send file when a new one is selected ----
  if (formData.student_image instanceof File) {
    payload.append("student_image", formData.student_image);
  }

  // ---- password optional ----
  if (formData.password.trim()) {
    payload.append("password", formData.password);
  }

  fetch(SummaryApi.updateStudent.url, {
    method: SummaryApi.updateStudent.method || "POST",
    body: payload,
  })
    .then((r) => r.json())
    .then((resp) => {
            setLoading(false);
      if (resp.status) {
        Swal.fire({
          icon: "success",
          title: "Saved",
          text: "Profile updated",
          timer: 2000,
          showConfirmButton: false,
        }).then(() => {
          // Try to close the tab (works only if opened via window.open)
          const closed = window.close();

          // Fallback: if tab not closed (user-opened tab), navigate back
          if (!closed) {
            navigate("/student-panel/profile");
          }
        });
      } else {
        Swal.fire({ icon: "error", title: "Failed", text: resp.message || "Unknown error" });
      }
    })
    .catch((err) => {
       setLoading(false);
      console.error(err);
      Swal.fire({ icon: "error", title: "Error", text: "Network problem" });
    });
};

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-900 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto p-6 bg-white dark:bg-gray-800 rounded-xl shadow-lg mt-10">
        <h2 className="text-2xl font-semibold mb-6 text-[#1f4774] dark:text-white">
          Edit Student Profile
        </h2>

        <form
          onSubmit={handleSubmit}
          className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6"
        >
          {/* ==== BASIC FIELDS ==== */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              required
              className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-3 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#1f4774] dark:focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Last Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              required
              className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-3 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#1f4774] dark:focus:ring-blue-500"
            />
          </div>

          {/* ==== PASSWORD (optional) ==== */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Password <span className="text-red-500">*</span>
            </label>
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Leave blank to keep current"
              className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-3 pr-10 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#1f4774] dark:focus:ring-blue-500"
            />
            <button
              type="button"
              onClick={() => setShowPassword((p) => !p)}
              className="absolute inset-y-0 right-3 flex items-center text-gray-600 dark:text-gray-400"
            >
              {showPassword ? <FaEyeSlash size={20} /> : <FaEye size={20} />}
            </button>
          </div>

          {/* ==== MOBILE NUMBER (10 digits, numbers only) ==== */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Mobile Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              name="mobile_number"
              value={formData.mobile_number}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, "").slice(0, 10);
                setFormData((prev) => ({ ...prev, mobile_number: value }));
              }}
              maxLength={10}
              required
              placeholder="Enter 10-digit mobile number"
              className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-3 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#1f4774] dark:focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {formData.mobile_number.length}/10 digits
            </p>
          </div>

          {/* ==== ALTERNATE MOBILE NUMBER (10 digits, numbers only) ==== */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Alternate Mobile Number
            </label>
            <div className="relative">
              <input
                type="text"
                name="alternate_mobile_number"
                value={formData.alternate_mobile_number}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "");
                  if (value.length <= 10) {
                    setFormData({
                      ...formData,
                      alternate_mobile_number: value,
                    });
                  }
                }}
                maxLength="10"
                className="block w-full border border-gray-300 dark:border-gray-600 rounded-md p-1.5 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:border-[#1f4774] dark:focus:border-blue-500 focus:ring-2 focus:ring-[#1f4774] dark:focus:ring-blue-500 text-sm"
              />
            </div>
          </div>

          {/* ==== REMAINING FIELDS (non-mobile) ==== */}
          {[
            { label: "UG/PG", name: "ug_pg" },
            { label: "Academic Qualification", name: "academic_course" },
            { label: "Academic Year", name: "academic_year" },
            { label: "Email", name: "email", required: true, type: "email" },
            { label: "Address", name: "address" },
            { label: "City", name: "city" },
{ 
    label: "University/College Name", 
    name: "college_name",
    readOnly: formData.is_self_registered === 0 && formData.college_id > 0
},
          ].map((f) => (
            <div key={f.name}>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {f.label} {f.required && <span className="text-red-500">*</span>}
              </label>
              <input
                type={f.type || "text"}
                name={f.name}
                value={formData[f.name]}
                onChange={handleChange}
                required={!!f.required}
                readOnly={f.readOnly} 
                className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-3 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#1f4774] dark:focus:ring-blue-500"
              />
            </div>
          ))}

          {/* ==== IMAGE UPLOAD & PREVIEW ==== */}
{/* ==== IMAGE UPLOAD & PREVIEW ==== */}
<div className="md:col-span-2">
  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
    Profile Image
  </label>
  <div className="relative border border-gray-300 dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-700 min-h-[42px] flex items-center">
    <input
      type="file"
      accept="image/*"
      name="student_image"
      onChange={handleChange}
      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
    />
    <div className="w-full px-3 py-3 flex items-center justify-between">
      <span className="text-gray-500 dark:text-gray-400 text-sm">
        {formData.student_image instanceof File ? formData.student_image.name : "Choose a file..."}
      </span>
      <span className="bg-[#1f4774] dark:bg-blue-600 text-white text-sm font-semibold px-4 py-1 rounded hover:bg-[#16375c] dark:hover:bg-blue-700 transition">
        Browse
      </span>
    </div>
  </div>

  {/* PREVIEW – existing URL OR new blob */}
  {(formData.previewImage || formData.student_image) && (
    <div className="mt-4">
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Preview:</p>
      <img
        src={formData.previewImage || formData.student_image}
        alt="Student"
        onError={(e) => (e.currentTarget.src = noimage)}
        className="w-32 h-32 object-cover rounded border border-gray-300 dark:border-gray-600"
      />
    </div>
  )}
</div>

          {/* ==== ACTIVE TOGGLE ==== */}

          {/* ==== SUBMIT ==== */}
          <div className="md:col-span-2 text-right">
            <button
              type="submit"
              className="bg-[#1f4774] dark:bg-blue-700 text-white font-semibold px-6 py-3 rounded hover:bg-[#16375c] dark:hover:bg-blue-800 transition shadow-md"
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StudentProfileEdit;